import React from "react";
import { Dialog, DialogContent, DialogTitle } from "~/components/dialog";
import { useSnackBar } from "~/components/snackbar";
import Spinner from "~/components/spinner";
import { Project, SharedDrive } from "~/gql/types2";
import {
  useAddProjectToGroupedFolderMutation,
  useCreateGroupedFolderForProjectMutation,
  useCreateProjectFolderMutation,
} from "./mutation.hotchoc.generated";
import styled from "@emotion/styled";
import { useReloadProject } from "~/visuals/pages/Project/ProjectRouter";
import {
  InlineFieldsForm,
  InlineFieldsFormSubmitElement,
} from "~/components/inline-fields-form";
import { ValidationResponse } from "~/gql/types";
import { FolderFormData, initialFieldState, updater } from "./FolderFormUtils";
import { AddFolderFormFields } from "./AddFolderFormFields";
import { FormStateRenderer } from "~/components/inline-fields-form/FormStateContext";
import { FolderRequestType } from "../types";
import { FormState } from "~/components/inline-fields-form/types";
import Button from "~/components/button";

const FullWidthDialog = styled(Dialog)`
  & .mdc-dialog__surface {
    width: 560px;
  }
`;

const SubmitElementWrapper = styled("div")`
  display: flex;
  align-items: center;
  justify-content: right;
  padding: 12px;
  margin-top: 3rem;
`;

type AddFolderDialogProps = {
  open: boolean;
  setOpen: React.Dispatch<boolean>;
  project: Project;
  folderDrives: SharedDrive[];
  drivesLoading: boolean;
};

export const AddFolderFormDialog: React.FC<AddFolderDialogProps> = ({
  open,
  setOpen,
  project,
  folderDrives,
  drivesLoading,
}) => {
  const reload = useReloadProject();

  const addAlert = useSnackBar();

  const [createProjectFolder, { loading: createLoading }] =
    useCreateProjectFolderMutation();
  const [createGroupedFolder, { loading: createGroupedLoading }] =
    useCreateGroupedFolderForProjectMutation();
  const [addProjectToGroupedFolder, { loading: addProjectLoading }] =
    useAddProjectToGroupedFolderMutation();

  const handleFormResult = (data: ValidationResponse): string | void => {
    if (data.errors && data.errors?.length > 0) {
      const messages: string[] = [];
      data.errors.forEach((e) => {
        if (e?.argumentName) {
          messages.push(`${e.argumentName}: ${e.message}`);
        } else if (e?.message) {
          messages.push(e.message);
        }
      });
      return messages.join(" ");
    } else {
      setOpen(false);
      addAlert({
        key: `create folder ${project.number}`,
        message: `Added folder for project number ${project.number}`,
        isSuccess: true,
      });
      reload();
    }
  };

  const runMutation = async (formState: FormState) => {
    const values = {} as FolderFormData;

    Object.values(formState.fields).forEach(({ name, value }) => {
      values[name] = value;
    });

    let data: ValidationResponse = {};
    if (values.folderType == FolderRequestType.Single) {
      const res = await createProjectFolder({
        variables: {
          input: {
            driveLetter: values.projectFolderDriveLetter!,
            projectNumber: project.number,
          },
        },
      });
      data = res.data?.projectFolders?.create as ValidationResponse;
    }

    if (values.folderType == FolderRequestType.ExistingGrouped) {
      const res = await addProjectToGroupedFolder({
        variables: {
          input: {
            groupedFolderId: values.groupedFolder!.id,
            projectNumber: project.number,
          },
        },
      });
      data = res.data?.projectFolders
        ?.addProjectToGroupedFolder as ValidationResponse;
    }

    if (values.folderType == FolderRequestType.NewGrouped) {
      const res = await createGroupedFolder({
        variables: {
          input: {
            driveLetter: values.newGroupedFolderDriveLetter!,
            folderName: values.newGroupedFolderName!,
            projectNumber: project.number,
          },
        },
      });
      data = res.data?.projectFolders
        ?.createGroupedFolderForProject as ValidationResponse;
    }

    const errorResponse = handleFormResult(data);
    if (errorResponse) {
      setOpen(false);
      addAlert({
        isSuccess: false,
        message: errorResponse,
        key: `${Math.random()}`,
      });
    }
  };

  const SubmitElement: InlineFieldsFormSubmitElement = ({ onFormSubmit }) => {
    return (
      <SubmitElementWrapper>
        <Button primary onClick={() => onFormSubmit(runMutation)}>
          Save
        </Button>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
      </SubmitElementWrapper>
    );
  };

  return (
    <FullWidthDialog open={open} onClose={() => setOpen(false)} portalize>
      {drivesLoading ||
      createLoading ||
      createGroupedLoading ||
      addProjectLoading ? (
        <Spinner open={drivesLoading} />
      ) : (
        <>
          <DialogTitle>
            Create a new folder, or add this project to an existing one
          </DialogTitle>
          <DialogContent>
            <InlineFieldsForm
              initialFieldState={initialFieldState}
              handleSubmit={runMutation}
              updater={updater}
              SubmitElement={SubmitElement}
            >
              <FormStateRenderer
                render={(formState) => (
                  <AddFolderFormFields
                    formState={formState}
                    customerId={project.customer.customerId}
                    folderDrives={folderDrives}
                  />
                )}
              />
            </InlineFieldsForm>
          </DialogContent>
        </>
      )}
    </FullWidthDialog>
  );
};
